import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RotateCcw, ArrowLeft } from 'lucide-react';

interface RouteErrorBoundaryProps {
  children: ReactNode;
  /**
   * Optional label for the error context (e.g. "Providers" or "Orders").
   * Shown in the error message to help operators identify the broken surface.
   */
  context?: string;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  /** Incremented on retry to force a remount of children */
  retryKey: number;
  /** True when the error looks like a failed dynamic import (chunk load) */
  isChunkError: boolean;
}

/**
 * Route-level React error boundary for admin pages.
 *
 * Catches render-phase errors that would otherwise leave the user on a blank
 * admin shell (sidebar+header visible, content area empty) or escalate to a
 * browser chrome-error page.
 *
 * Design principles (UI/UX standard):
 *  - Show a contained error state inside the page content area — never a
 *    full-page blank or browser native error.
 *  - Give actionable escapes: "Reload page" (for data/chunk errors) and
 *    "Go back" (for navigation errors).
 *  - For chunk-load failures specifically, offer a targeted reload so the
 *    browser re-downloads the fresh JS chunk.
 *  - Respect the existing design language (shadcn/ui Button, neutral palette).
 */
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      retryKey: 0,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<RouteErrorBoundaryState> {
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      /loading chunk/i.test(error.message) ||
      /failed to fetch dynamically imported module/i.test(error.message) ||
      /error loading dynamically imported module/i.test(error.message);

    return { hasError: true, error, isChunkError };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log for debugging — in production this would go to an error-tracking service.
    console.error('[RouteErrorBoundary] caught render error:', error, info.componentStack);
  }

  private handleRetry = () => {
    this.setState((prev) => ({
      hasError: false,
      error: null,
      isChunkError: false,
      retryKey: prev.retryKey + 1,
    }));
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleBack = () => {
    window.history.back();
  };

  private renderErrorUI(
    icon: ReactNode,
    title: string,
    description: string,
    primaryLabel: string,
    onPrimary: () => void,
    secondaryLabel: string,
    onSecondary: () => void,
  ) {
    return (
      <div
        role="alert"
        aria-live="assertive"
        className="flex min-h-[320px] flex-col items-center justify-center px-6 py-16 text-center"
      >
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
          {icon}
        </div>

        <h2 className="text-[16px] font-semibold text-ink">{title}</h2>

        <p className="mt-2 max-w-sm text-[12.5px] text-muted-foreground">{description}</p>

        <div className="mt-6 flex items-center gap-3">
          <Button size="sm" onClick={onPrimary}>
            {primaryLabel}
          </Button>
          <Button size="sm" variant="outline" onClick={onSecondary}>
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {secondaryLabel}
          </Button>
        </div>
      </div>
    );
  }

  render() {
    const { hasError, error, retryKey, isChunkError } = this.state;
    const { children } = this.props;

    if (!hasError) {
      return <div key={retryKey}>{children}</div>;
    }

    // Chunk-load failure: stale JS bundle — the page needs a hard refresh to
    // pick up newly deployed chunks. Inline retry won't help.
    if (isChunkError) {
      return this.renderErrorUI(
        <RotateCcw className="h-8 w-8 text-muted-foreground" aria-hidden="true" />,
        'Update available',
        "The page couldn't load because a newer version of the app was deployed. Reload to get the latest version.",
        'Reload page',
        this.handleReload,
        'Go back',
        this.handleBack,
      );
    }

    // Generic render error.
    return this.renderErrorUI(
      <AlertTriangle className="h-8 w-8 text-amber-500" aria-hidden="true" />,
      'Something went wrong',
      error?.message
        ? `An error occurred while rendering this page: ${error.message}`
        : 'An unexpected error occurred while rendering this page.',
      'Try again',
      this.handleRetry,
      'Go back',
      this.handleBack,
    );
  }
}
